import { useState } from "react";
import { AnimatedSection } from "@/components/AnimatedSection";
import { FloatingCard } from "@/components/FloatingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CardContent } from "@/components/ui/card";
import { Link } from "wouter";
import { Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

export default function ResetPassword() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-card to-background p-4">
        <AnimatedSection direction="up">
          <FloatingCard>
            <CardContent className="pt-8 pb-8 space-y-4 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto" />
              <h1 className="text-xl font-bold">Ungültiger Link</h1>
              <p className="text-muted-foreground text-sm">
                Dieser Link ist ungültig oder abgelaufen.
              </p>
              <Button asChild>
                <Link href="/login">Zur Anmeldung</Link>
              </Button>
            </CardContent>
          </FloatingCard>
        </AnimatedSection>
      </div>
    );
  }

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein");
      return;
    }

    setState("loading");
    try {
      await apiRequest("POST", "/api/auth/reset-password", { token, password });
      setState("success");
    } catch (err: any) {
      setState("error");
      setError(err.data?.error || "Zurücksetzen fehlgeschlagen");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-card to-background p-4">
      <div className="w-full max-w-md">
        <AnimatedSection direction="up">
          <FloatingCard>
            <CardContent className="pt-8 pb-8 space-y-6">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-2xl font-bold font-playfair">Neues Passwort</h1>
                <p className="text-muted-foreground text-sm">Wählen Sie ein neues Passwort für Ihr Konto</p>
              </div>

              {state === "success" ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-700 dark:text-green-300">
                      <p className="font-medium">Passwort geändert!</p>
                      <p>Sie können sich jetzt mit Ihrem neuen Passwort anmelden.</p>
                    </div>
                  </div>
                  <Button asChild className="w-full">
                    <Link href="/login">Zur Anmeldung</Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleReset} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Neues Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="new-password"
                        type="password"
                        placeholder="Min. 8 Zeichen, 1 Großbuchstabe, 1 Zahl"
                        className="pl-9"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={8}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Passwort bestätigen</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm-password"
                        type="password"
                        placeholder="Passwort wiederholen"
                        className="pl-9"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <Button type="submit" className="w-full" disabled={state === "loading"}>
                    {state === "loading" ? "Speichern..." : "Passwort speichern"}
                  </Button>
                </form>
              )}
            </CardContent>
          </FloatingCard>
        </AnimatedSection>
      </div>
    </div>
  );
}
